import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { Container } from "reactstrap";
import { useHistory, Link } from "react-router-dom";

import Header from "./Header";
import typography from "../typography";
import FilterModal from "../FilterModal";
import { Heading } from "components/Heading";
import { convertDateTime, dateTimeFormat } from "utils/dateFormatUtils";
import { APP_ROUTES } from "../../../helpers/routeHelpers";
import { Pagination } from "../../../components/Pagination";
import DataTable from "../../../components/DataTable/DataTable";
import { getClickAnalyticsListing } from "../../../api/api.service";
import {
  DeviceType,
  PageHeadings,
  PageTitles,
  SortOrder,
  SourceType,
} from "../../../constants";
import { useLocalPaginatedRequest } from "../../../hooks/useLocalPaginatedRequest";

import viewIcon from "../../../assets/images/app/common/aou-view-icon.svg";

const initialFilters = {
  fromDate: null,
  toDate: null,
  devices: {
    ios: false,
    android: false,
    web: false,
  },
  sources: {
    facebook: false,
    instagram: false,
    other: false,
  },
  country: "",
  countryCode: "",
};

const Listing = () => {
  const history = useHistory();
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState(initialFilters);
  const [currentSortColumn, setCurrentSortColumn] = useState(null);
  const [currentSortOrder, setCurrentSortOrder] = useState(null);

  const params = {
    ...(currentSortColumn &&
      currentSortOrder && {
        column: currentSortColumn,
        direction: currentSortOrder,
      }),
    // Add date filters
    ...(filters.fromDate && {
      StartDate: convertDateTime({
        date: filters.fromDate,
        customFormat: dateTimeFormat.apiDateFormat,
      }),
    }),
    ...(filters.toDate && {
      EndDate: convertDateTime({
        date: filters.toDate,
        customFormat: dateTimeFormat.apiDateFormat,
      }),
    }),
    // Add device filters
    ...(() => {
      const selectedDevices = [];
      if (filters.devices.ios) selectedDevices.push(DeviceType.IOS);
      if (filters.devices.android) selectedDevices.push(DeviceType.ANDROID);
      if (filters.devices.web) selectedDevices.push(DeviceType.WEB);
      return selectedDevices.length > 0 ? { Device: selectedDevices } : {};
    })(),
    // Add source filters
    ...(() => {
      const selectedSources = [];
      if (filters.sources.facebook) selectedSources.push(SourceType.FACEBOOK);
      if (filters.sources.instagram) selectedSources.push(SourceType.INSTAGRAM);
      if (filters.sources.other) selectedSources.push(SourceType.OTHER);
      return selectedSources.length > 0 ? { Source: selectedSources } : {};
    })(),
    // Add country filter
    ...(filters.countryCode && { Country: [filters.countryCode] }),
  };

  const {
    isFetching,
    page,
    total,
    data,
    handlePageClick,
    onChangePageSize,
    pageSize,
    setPageToOne,
  } = useLocalPaginatedRequest(
    getClickAnalyticsListing,
    params,
    [filters, currentSortOrder],
    "Q",
    "data",
    "totalCount"
  );

  const handleFilterClick = () => {
    setIsFilterModalOpen(true);
  };

  const handleFilterApply = (newFilters) => {
    setFilters(newFilters);
    setPageToOne();
    setIsFilterModalOpen(false);
  };

  const handleViewDetails = (date) => {
    history.push(`${APP_ROUTES.CLICK_ANALYTICS_DETAILS}/${date}`);
  };

  const handleSortClick = (item) => {
    setCurrentSortColumn(item.sortColumn);
    setCurrentSortOrder((prev) =>
      prev === SortOrder.ASC || !prev ? SortOrder.DESC : SortOrder.ASC
    );
  };

  return (
    <>
      <div className="page-content">
        <Helmet>
          <title>{PageTitles.CLICK_ANALYTICS}</title>
        </Helmet>
        <Container fluid className="bls-feedback click-analytics">
          <Heading heading={PageHeadings.CLICK_ANALYTICS} />
          <Header onFilterClick={handleFilterClick} />
          <DataTable
            data={data}
            loading={isFetching}
            currentSortOrder={currentSortOrder}
            currentSortColumn={currentSortColumn}
            onSortClick={handleSortClick}
            config={[
              {
                isSortable: false,
                title: typography.SERIAL_NUMBER,
                render: (data, index) => {
                  const serialNumber = (page - 1) * pageSize + index + 1;
                  return (
                    <div className="standard-typo-14-400">
                      {String(serialNumber).padStart(2, "0")}
                    </div>
                  );
                },
              },
              {
                isSortable: true,
                title: typography.DATE_TIME,
                sortColumn: "day",
                render: (data) => (
                  <div className="standard-typo-14-400">
                    {convertDateTime({
                      date: data?.day,
                      customFormat: dateTimeFormat.americanFormat,
                    }) || "-"}
                  </div>
                ),
              },
              {
                isSortable: true,
                title: typography.TOTAL_CLICKS,
                sortColumn: "totalClicks",
                render: (data) => (
                  <div className="standard-typo-14-400">
                    {data?.totalClicks || "-"}
                  </div>
                ),
              },
              {
                title: typography.ACTION,
                render: (data) => {
                  return (
                    <div className="d-flex">
                      <Link
                        className="view-form-icon-con"
                        to={`${
                          APP_ROUTES.CLICK_ANALYTICS_DETAILS
                        }/${convertDateTime({
                          date: data.day,
                          customFormat: dateTimeFormat.apiDateFormat,
                        })}`}
                      >
                        <img src={viewIcon} alt={"view icon"} role="button" />
                      </Link>
                    </div>
                  );
                },
              },
            ]}
          />
          <Pagination
            onPageClick={handlePageClick}
            currentPage={page}
            totalData={total}
            handleChange={(e) => onChangePageSize(e)}
            rowLimit={pageSize}
          />
        </Container>
      </div>
      {isFilterModalOpen && (
        <FilterModal
          isOpen={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          onApply={handleFilterApply}
          initialFilters={filters}
        />
      )}
    </>
  );
};

export default Listing;
